import React, { useState, useEffect, useRef } from 'react';
import {
  Container, Paper, Typography, TextField, Button, Box, MenuItem, Select, FormControl, InputLabel, Grid, Alert, IconButton
} from '@mui/material';
import { Add, Remove, ArrowBack } from '@mui/icons-material';
import api from '../../services/api';
import { useNavigate } from 'react-router-dom';
import BSDateInput from '../common/BSDateInput';

const karatOptions = ['22K', '24K', '22/22K', '24/24K', '18K', 'PT95', 'WG'];
const unitOptions = ['Pcs', 'Pair', 'Set'];

const emptyItem = () => ({ itemName: '', karat: '', quantity: '', unit: '', itemWeight: '', description: '', wastage: '' });

const DirectOrder = () => {
  const [karigar, setKarigar] = useState('');
  const [karigars, setKarigars] = useState([]);
  const [salesman, setSalesman] = useState('');
  const [order, setOrder] = useState({ billNumber: '', orderDate: '', deliveryDate: '', requiredDate: '' });
  const [items, setItems] = useState([emptyItem()]);
  const [itemOptions, setItemOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [directOrderExists, setDirectOrderExists] = useState(false);
  const [shouldFetchDirectOrder, setShouldFetchDirectOrder] = useState(false);
  const lastAttemptedBillNumber = useRef('');

  const navigate = useNavigate();

  useEffect(() => {
    api.get('/api/karigar').then(res => setKarigars(res.data)).catch(() => {});
    api.get('/api/items').then(res => setItemOptions(res.data.map(i => i.name))).catch(() => {});
    const storedBillNumber = localStorage.getItem('sharedBillNumber');
    if (storedBillNumber) setOrder(o => ({ ...o, billNumber: storedBillNumber }));
  }, []);

  useEffect(() => {
    if (shouldFetchDirectOrder && order.billNumber) {
      api.get(`/api/directorders?billNumber=${encodeURIComponent(order.billNumber)}`)
        .then(res => {
          setDirectOrderExists(Array.isArray(res.data) && res.data.length > 0);
        })
        .catch(() => setDirectOrderExists(false));
      setShouldFetchDirectOrder(false);
    }
  }, [shouldFetchDirectOrder, order.billNumber]);

  const handleOrderChange = e => {
    const { name, value } = e.target;
    setOrder(o => ({ ...o, [name]: value }));
    if (name === 'billNumber') {
      localStorage.setItem('sharedBillNumber', value);
    }
  };

  const handleItemChange = (idx, e) => {
    const { name, value } = e.target;
    setItems(items => items.map((item, i) => i === idx ? { ...item, [name]: value } : item));
  };

  const addItem = () => setItems(items => [...items, emptyItem()]);
  const removeItem = idx => setItems(items => items.length > 1 ? items.filter((_, i) => i !== idx) : items);

  const validate = () => {
    if (!karigar) return 'Karigar is required.';
    if (!salesman) return 'Salesman is required.';
    if (!order.billNumber || !order.orderDate || !order.deliveryDate || !order.requiredDate) return 'All order fields are required.';
    for (const item of items) {
      if (!item.itemName || !item.karat || !item.quantity || !item.unit || !item.itemWeight) return 'All item fields are required.';
    }
    return '';
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setError(''); setSuccess('');
    const err = validate();
    if (err) { setError(err); return; }
    if (directOrderExists) { setError('This bill already exists. Use Update Direct Order to edit.'); return; }
    setLoading(true);
    try {
      const computedItems = items.map(item => {
        const weight = parseFloat(item.itemWeight) || 0;
        const wastage = parseFloat(item.wastage) || 0;
        const rate = parseFloat(item.rate) || 0;
        const makingCharge = parseFloat(item.makingCharge) || 0;
        const stoneAmount = parseFloat(item.stoneAmount) || 0;
        const total = weight + (weight * wastage / 100);
        const netAmount = weight ? ((weight / 11.664) * rate) : 0;
        const totalAmount = netAmount + makingCharge + stoneAmount;
        return {
          ...item,
          quantity: Number(item.quantity),
          itemWeight: Number(item.itemWeight),
          wastage: wastage,
          total: parseFloat(total.toFixed(3)),
          netAmount: parseFloat(netAmount.toFixed(2)),
          totalAmount: parseFloat(totalAmount.toFixed(2)),
          gsb: Number(item.gsb) || 0,
          rate: Number(item.rate) || 0,
          makingCharge: Number(item.makingCharge) || 0,
          stoneAmount: Number(item.stoneAmount) || 0,
          despatchStatus: false,
          shopDespatchStatus: false,
          workIssued: false,
          karigarAbbreviation: karigar
        };
      });
      const orderTotalAmount = computedItems.reduce((sum, item) => sum + (item.totalAmount || 0), 0);
      await api.post('/api/directorders', {
        billNumber: order.billNumber,
        orderDate: order.orderDate,
        deliveryDate: order.deliveryDate,
        requiredDate: order.requiredDate,
        status: 'Pending',
        agentName: salesman,
        karigarAbbreviation: karigar,
        items: computedItems,
        totalAmount: orderTotalAmount,
        isUrgent: false,
        isActive: true
      });
      setSuccess('Direct order created successfully!');
      setKarigar(''); setSalesman('');
      setOrder({ billNumber: '', orderDate: '', deliveryDate: '', requiredDate: '' });
      setItems([emptyItem()]);
      localStorage.removeItem('sharedBillNumber');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create direct order');
    }
    setLoading(false);
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Button startIcon={<ArrowBack />} onClick={() => window.history.back()} variant="outlined" size="small">Back</Button>
          <Button variant="outlined" color="secondary" size="small" onClick={() => navigate('/update-direct-order')}>Update Existing</Button>
        </Box>
        <Typography variant="h5" gutterBottom color="primary">Karigar Direct Order Entry</Typography>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
        {directOrderExists && <Alert severity="warning" sx={{ mb: 2 }}>This bill already exists.</Alert>}
        <form onSubmit={handleSubmit}>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={2}>
              <FormControl fullWidth required size="small">
                <InputLabel>Karigar</InputLabel>
                <Select value={karigar} label="Karigar" onChange={e => setKarigar(e.target.value)}>
                  {karigars.map(k => <MenuItem key={k.abbreviation} value={k.abbreviation}>{k.abbreviation} - {k.name}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={2}>
              <TextField label="Salesman" value={salesman} onChange={e => setSalesman(e.target.value)} required fullWidth size="small" />
            </Grid>
            <Grid item xs={12} sm={2}>
              <TextField label="Bill Number" name="billNumber" value={order.billNumber}
                onChange={handleOrderChange}
                onBlur={e => { lastAttemptedBillNumber.current = e.target.value; setShouldFetchDirectOrder(true); }}
                required fullWidth size="small" />
            </Grid>
            <Grid item xs={12} sm={2}>
              <BSDateInput label="Order Date" value={order.orderDate} onChange={date => setOrder(o => ({ ...o, orderDate: date }))} required />
            </Grid>
            <Grid item xs={12} sm={2}>
              <BSDateInput label="Delivery Date" value={order.deliveryDate} onChange={date => setOrder(o => ({ ...o, deliveryDate: date }))} required />
            </Grid>
            <Grid item xs={12} sm={2}>
              <BSDateInput label="Required Date" value={order.requiredDate} onChange={date => setOrder(o => ({ ...o, requiredDate: date }))} required />
            </Grid>
          </Grid>

          <Typography variant="subtitle1" sx={{ mt: 1, mb: 1 }}>Order Items</Typography>
          {items.map((item, idx) => (
            <Box key={idx} sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 1.5, mb: 1.5 }}>
              <Grid container spacing={1} alignItems="center">
                <Grid item xs={6} sm={2}>
                  <FormControl fullWidth required size="small">
                    <InputLabel>Item Name</InputLabel>
                    <Select name="itemName" value={item.itemName} label="Item Name" onChange={e => handleItemChange(idx, e)}>
                      {itemOptions.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6} sm={1}>
                  <FormControl fullWidth required size="small">
                    <InputLabel>Karat</InputLabel>
                    <Select name="karat" value={item.karat} label="Karat" onChange={e => handleItemChange(idx, e)}>
                      {karatOptions.map(k => <MenuItem key={k} value={k}>{k}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={4} sm={1}>
                  <TextField label="Qty" name="quantity" type="number" value={item.quantity} onChange={e => handleItemChange(idx, e)} required fullWidth size="small" />
                </Grid>
                <Grid item xs={4} sm={1}>
                  <FormControl fullWidth required size="small">
                    <InputLabel>Unit</InputLabel>
                    <Select name="unit" value={item.unit} label="Unit" onChange={e => handleItemChange(idx, e)}>
                      {unitOptions.map(u => <MenuItem key={u} value={u}>{u}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={4} sm={1}>
                  <TextField label="Weight" name="itemWeight" type="number" value={item.itemWeight} onChange={e => handleItemChange(idx, e)} required fullWidth size="small" />
                </Grid>
                <Grid item xs={6} sm={1}>
                  <TextField label="Wastage %" name="wastage" type="number" value={item.wastage} onChange={e => handleItemChange(idx, e)} fullWidth size="small" />
                </Grid>
                <Grid item xs={6} sm={1}>
                  <TextField
                    label="Wastage Wt"
                    value={item.itemWeight && item.wastage ? ((parseFloat(item.itemWeight) * parseFloat(item.wastage)) / 100).toFixed(3) : ''}
                    InputProps={{ readOnly: true }}
                    fullWidth size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField label="Description" name="description" value={item.description} onChange={e => handleItemChange(idx, e)} fullWidth size="small" />
                </Grid>
                <Grid item xs={12} sm={1}>
                  <Box sx={{ display: 'flex' }}>
                    <IconButton size="small" onClick={() => removeItem(idx)} disabled={items.length === 1}><Remove fontSize="small" /></IconButton>
                    <IconButton size="small" onClick={addItem}><Add fontSize="small" /></IconButton>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          ))}

          <Box sx={{ mt: 2 }}>
            <Button type="submit" variant="contained" color="primary" disabled={loading}>
              {loading ? 'Saving...' : 'Save Order'}
            </Button>
          </Box>
        </form>
      </Paper>
    </Container>
  );
};

export default DirectOrder;
